/**
 * Child Form Component
 * Add/Edit dialog content for children
 */
import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, Trash2, Shield, Mail, Phone, AlertTriangle, Lock, Camera, Upload, X, Info, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { usePhotoRenewal } from "@/hooks/usePhotoRenewal";
import { plannerApi } from "@/components/tour-planner/plannerApi";
import { PLANNER_CATEGORIES } from "@/constants/plannerCategories";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Photo preview component with error handling + renewal
const PhotoPreview = ({ childId, photoUrl, onDelete }) => {
  const { src, failed, handleError } = usePhotoRenewal(childId, photoUrl);
  
  if (!src || failed) {
    return (
      <div className="w-16 h-16 rounded-full bg-gray-200 border-2 border-dashed border-gray-300 flex items-center justify-center">
        <Camera className="w-6 h-6 text-gray-400" />
      </div>
    );
  }
  
  return (
    <div className="relative">
      <img
        src={src}
        alt="Child"
        className="w-16 h-16 rounded-full object-cover border-2 border-blue-300"
        onError={handleError}
      />
      <button
        type="button"
        onClick={onDelete}
        className="absolute -top-1 -right-1 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600 transition-colors"
        title="Remove photo"
      >
        <X className="w-3 h-3" />
      </button>
    </div>
  );
};

export const ChildForm = ({
  open,
  onOpenChange,
  formData,
  setFormData,
  onSubmit,
  editingChild,
  onAddPickupPerson,
  onAddAuthorizedPickup,
  onRemovePickupPerson,
  onPickupPersonChange,
  onAddEmergencyContact,
  onRemoveEmergencyContact,
  onEmergencyContactChange,
  onHealthInsuranceChange,
  onPhysicianChange,
  organizationId,
  onPhotoUpdate
}) => {
  // Pick Up Safety Alerts state
  const { token } = useAuth();
  const [alertSettings, setAlertSettings] = useState({
    emailAlertEnabled: false,
    smsAlertEnabled: false,
    alertEmail: '',
    alertPhone: '',
    alertPhoneEdited: false,
    originalPhone: ''
  });
  const [smsSubscribed, setSmsSubscribed] = useState(false);
  const [loadingAlerts, setLoadingAlerts] = useState(false);
  
  // Photo upload state
  const [photoUrl, setPhotoUrl] = useState(null);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const fileInputRef = useRef(null);

  // Schools list (director-maintained via "Manage Schools") — for the School Name
  // dropdown below, fetched best-effort same as plannerRooms.
  const [schoolsList, setSchoolsList] = useState([]);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  const [schoolsLoadError, setSchoolsLoadError] = useState(false);
  const loadSchools = () => {
    if (!open) return;
    setSchoolsLoading(true);
    setSchoolsLoadError(false);
    axios.get(`${API}/schools`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => setSchoolsList(res.data || []))
      .catch(() => setSchoolsLoadError(true)) // keep last-known schoolsList — never blank the saved value
      .finally(() => setSchoolsLoading(false));
  };
  useEffect(() => { loadSchools(); }, [open, token]);

  // Tour & Ratio Planner rooms — for the optional "Room for Ratios" override below. Fetched
  // best-effort: a staff member without Tour Planner access simply won't see this field (no rooms
  // to choose from), same as if the org doesn't use the planner at all.
  const [plannerRooms, setPlannerRooms] = useState([]);
  useEffect(() => {
    if (!open) return;
    plannerApi.listRooms().then((res) => setPlannerRooms(res.data || [])).catch(() => setPlannerRooms([]));
  }, [open]);
  
  // Initialize photo from editing child
  useEffect(() => {
    if (editingChild?.photoUrl) {
      setPhotoUrl(editingChild.photoUrl);
    } else {
      setPhotoUrl(null);
    }
  }, [editingChild]);
  
  // Fetch alert settings when editing a child
  useEffect(() => {
    if (editingChild?.id) {
      fetchAlertSettings(editingChild.id);
    } else {
      // Reset for new child - auto-populate from first pickup person
      const firstPerson = formData.pickupPersons?.[0];
      setAlertSettings({
        emailAlertEnabled: false,
        smsAlertEnabled: false,
        alertEmail: firstPerson?.email || '',
        alertPhone: firstPerson?.phone || '',
        alertPhoneEdited: false,
        originalPhone: firstPerson?.phone || ''
      });
    }
  }, [editingChild?.id]);
  
  // Check if organization has SMS subscription
  useEffect(() => {
    if (organizationId) {
      checkSubscriptions();
    }
  }, [organizationId]);
  
  const [emailSubscribed, setEmailSubscribed] = useState(true);
  
  // Auto-update alert contact from first pickup person when it changes
  useEffect(() => {
    const firstPerson = formData.pickupPersons?.[0];
    if (firstPerson && !editingChild) {
      setAlertSettings(prev => ({
        ...prev,
        alertEmail: prev.alertEmail || firstPerson.email || '',
        alertPhone: prev.alertPhone || firstPerson.phone || '',
        originalPhone: firstPerson.phone || ''
      }));
    }
  }, [formData.pickupPersons, editingChild]);
  
  const fetchAlertSettings = async (childId) => {
    try {
      setLoadingAlerts(true);
      const response = await axios.get(`${API}/pickup-safety/child/${childId}`);
      setAlertSettings({
        emailAlertEnabled: response.data.emailAlertEnabled || false,
        smsAlertEnabled: response.data.smsAlertEnabled || false,
        alertEmail: response.data.alertEmail || '',
        alertPhone: response.data.alertPhone || '',
        alertPhoneEdited: response.data.alertPhoneEdited || false,
        originalPhone: response.data.originalPhone || ''
      });
    } catch (error) {
      console.error("Error fetching alert settings:", error);
    } finally {
      setLoadingAlerts(false);
    }
  };
  
  const checkSubscriptions = async () => {
    try {
      const response = await axios.get(`${API}/pickup-safety/settings?organizationId=${organizationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSmsSubscribed(response.data.smsSubscribed || false);
      setEmailSubscribed(response.data.emailSubscribed !== false); // Default to true if not set
    } catch (error) {
      console.error("Error checking subscriptions:", error);
    }
  };
  
  const handleAlertSettingChange = (field, value) => {
    setAlertSettings(prev => {
      const updated = { ...prev, [field]: value };
      // Track if phone was edited from original
      if (field === 'alertPhone') {
        updated.alertPhoneEdited = value !== prev.originalPhone;
      }
      return updated;
    });
  };
  
  // Enhanced submit to save alert settings
  const handleSubmitWithAlerts = async (e) => {
    e.preventDefault();
    
    // Call the original onSubmit first
    await onSubmit(e);
    
    // If editing, also save alert settings
    if (editingChild?.id) {
      try {
        await axios.put(`${API}/pickup-safety/child/${editingChild.id}`, alertSettings);
      } catch (error) {
        console.error("Error saving alert settings:", error);
        if (error.response?.data?.detail) {
          toast.error(error.response.data.detail);
        }
      }
    }
  };

  // Photo upload handler
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload a JPEG, PNG, or WebP image');
      return;
    }
    
    // Validate file size (5MB max)
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Photo must be less than 5MB');
      return;
    }
    
    // Must have child ID to upload
    if (!editingChild?.id) {
      toast.error('Please save the child first before uploading a photo');
      return;
    }
    
    setUploadingPhoto(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await axios.post(
        `${API}/children/${editingChild.id}/photo`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      
      if (response.data.success) {
        setPhotoUrl(response.data.photoUrl);
        // Notify parent to update the children list
        if (onPhotoUpdate) {
          onPhotoUpdate(editingChild.id, response.data.photoUrl);
        }
        toast.success('Photo uploaded successfully');
      }
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Delete photo handler
  const handleDeletePhoto = async () => {
    if (!editingChild?.id) return;
    
    try {
      await axios.delete(`${API}/children/${editingChild.id}/photo`);
      setPhotoUrl(null);
      // Notify parent to update the children list
      if (onPhotoUpdate) {
        onPhotoUpdate(editingChild.id, null);
      }
      toast.success('Photo removed');
    } catch (error) {
      console.error('Error deleting photo:', error);
      toast.error('Failed to remove photo');
    }
  };

  return (
    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-2xl text-blue-800">
          {editingChild ? 'Edit Child' : 'Add New Child'}
        </DialogTitle>
      </DialogHeader>
      <form onSubmit={handleSubmitWithAlerts} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-blue-800">Child's Name *</Label>
            <Input
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="Enter child's name"
              className="border-blue-200"
              data-testid="child-name-input"
            />
          </div>
          <div>
            <Label className="text-blue-800">Birthdate</Label>
            <Input
              type="date"
              value={formData.birthdate || ''}
              onChange={(e) => setFormData({ ...formData, birthdate: e.target.value })}
              className="border-blue-200"
              data-testid="child-birthdate-input"
            />
          </div>
          <div>
            <Label className="text-blue-800">Start Date</Label>
            <Input
              type="date"
              value={formData.startDate || ''}
              onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
              className="border-blue-200"
              data-testid="child-startdate-input"
            />
          </div>
        </div>

        <div>
          <Label className="text-blue-800">Home Address</Label>
          <Input
            value={formData.address || ''}
            onChange={(e) => setFormData({ ...formData, address: e.target.value })}
            placeholder="Street, City, State, ZIP"
            className="border-blue-200"
            data-testid="child-address-input"
          />
        </div>

        {/* Photo Upload Section */}
        <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg border border-gray-200">
          {/* Photo Preview */}
          <div className="relative flex-shrink-0">
            <PhotoPreview childId={editingChild?.id} photoUrl={photoUrl} onDelete={handleDeletePhoto} />
          </div>
          
          {/* Upload Controls */}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <Label className="text-blue-800 text-sm font-medium">Child Photo</Label>
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Info className="w-4 h-4 text-gray-400 cursor-help" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-xs">
                    <p>Tap to take or upload a photo from your device. This photo will display on the Daily Sign In page to help staff identify children quickly.</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </div>
            
            <input
              ref={fileInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
              capture="environment"
              onChange={handlePhotoUpload}
              className="hidden"
              id="child-photo-input"
            />
            
            {editingChild?.id ? (
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => fileInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                {uploadingPhoto ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Uploading...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    {photoUrl ? 'Change Photo' : 'Upload Photo'}
                  </>
                )}
              </Button>
            ) : (
              <p className="text-xs text-gray-500 italic">Save child first to upload photo</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="text-blue-800">Category</Label>
            <Select
              value={formData.category || ""}
              onValueChange={(value) => setFormData({ ...formData, category: value, fullTimeSubcategory: value === 'full_time' ? formData.fullTimeSubcategory : '', plannerRoomOverride: '' })}
            >
              <SelectTrigger className="border-blue-200" data-testid="category-select">
                <SelectValue placeholder="Select category (optional)" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="full_time">Full Time</SelectItem>
                <SelectItem value="part_time">Part Time</SelectItem>
                <SelectItem value="school_age">School Age</SelectItem>
                <SelectItem value="after_school">After School</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {/* Full Time Subcategory - only show when Full Time is selected */}
          {(formData.category === 'full_time' || formData.category === 'regular') && (
            <div>
              <Label className="text-blue-800">Age Group</Label>
              <Select
                value={formData.fullTimeSubcategory || ""}
                onValueChange={(value) => setFormData({ ...formData, fullTimeSubcategory: value, plannerRoomOverride: '' })}
              >
                <SelectTrigger className="border-blue-200" data-testid="subcategory-select">
                  <SelectValue placeholder="Select age group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="prek">PreK</SelectItem>
                  <SelectItem value="older_toddler">Older Toddler</SelectItem>
                  <SelectItem value="young_toddler">Young Toddler</SelectItem>
                  <SelectItem value="infant">Infant</SelectItem>
                </SelectContent>
              </Select>
            </div>
          )}
          {(formData.category === 'school_age' || formData.category === 'after_school') && (
            <div>
              <Label className="text-blue-800">School Name</Label>
              <Select
                value={formData.school || ''}
                onValueChange={(value) => setFormData({ ...formData, school: value })}
              >
                <SelectTrigger className="border-blue-200" data-testid="school-select">
                  <SelectValue placeholder="Select school" />
                </SelectTrigger>
                <SelectContent>
                  {schoolsList.length === 0 && !schoolsLoadError && !schoolsLoading && (
                    <div className="px-2 py-1.5 text-xs text-gray-400" data-testid="school-select-empty">
                      No schools yet — add one via "Manage Schools" above
                    </div>
                  )}
                  {/* Preserve an existing legacy value even if it's not (yet) on the managed list
                      (or the list failed to load), so editing an older record never silently
                      blanks/hides their saved school. */}
                  {formData.school && !schoolsList.some((s) => s.name.trim().toLowerCase() === formData.school.trim().toLowerCase()) && (
                    <SelectItem value={formData.school} data-testid="school-option-legacy">
                      {formData.school}
                    </SelectItem>
                  )}
                  {schoolsList.map((s) => (
                    <SelectItem key={s.id} value={s.name} data-testid={`school-option-${s.id}`}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {schoolsLoadError && (
                <div className="flex items-center justify-between gap-2 mt-1 text-xs text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1" data-testid="school-select-load-error">
                  <span>Could not load schools</span>
                  <button type="button" onClick={loadSchools} className="underline font-medium" data-testid="school-select-retry-btn">
                    Retry
                  </button>
                </div>
              )}
              <p className="text-xs text-blue-600 mt-1">
                Required for School Age and After School children. Select their school to connect them to the
                correct bus pickup and drop-off records.
              </p>
            </div>
          )}
        </div>

        {plannerRooms.length > 0 && (
          (formData.category === 'full_time' || formData.category === 'regular') ? !!formData.fullTimeSubcategory : (formData.category === 'school_age' || formData.category === 'after_school')
        ) && (
          <div>
            <Label className="text-blue-800">Room for Ratios (Tour &amp; Ratio Planner)</Label>
            <Select
              value={formData.plannerRoomOverride || "__usual"}
              onValueChange={(value) => setFormData({ ...formData, plannerRoomOverride: value === "__usual" ? "" : value })}
            >
              <SelectTrigger className="border-blue-200" data-testid="planner-room-override-select">
                <SelectValue placeholder="Currently Assigned Room" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__usual">Currently Assigned Room</SelectItem>
                {plannerRooms
                  .filter((r) => r.category !== ((formData.category === 'full_time' || formData.category === 'regular') ? formData.fullTimeSubcategory : formData.category))
                  .map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.name} ({PLANNER_CATEGORIES.find((c) => c.value === r.category)?.label || r.category})
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
            <p className="text-sm text-blue-800 font-medium bg-blue-50 border border-blue-200 rounded-md px-3 py-2 mt-2">
              Only change this if the child is placed in a different room. This helps the Tour &amp; Ratio Planner count the child in the correct room and update ratios in that area without changing their actual enrollment category.
            </p>
          </div>
        )}

        <div>
          {/* ── Parents Section ── */}
          <div className="mb-3">
            <Label className="text-blue-800">Parents</Label>
          </div>
          <div className="space-y-4">
            {formData.pickupPersons.slice(0, 2).map((person, index) => (
              <Card key={index} className="border-blue-200">
                <CardContent className="pt-4">
                  <p className="text-xs font-semibold text-blue-500 uppercase tracking-wide mb-3">
                    {index === 0 ? 'Parent 1' : 'Parent 2'}
                  </p>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm text-blue-700">Name</Label>
                      <Input
                        value={person.name}
                        onChange={(e) => onPickupPersonChange(index, 'name', e.target.value)}
                        placeholder="Person's name"
                        className="border-blue-200"
                        data-testid={`pickup-name-${index}`}
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-blue-700">Phone</Label>
                      <Input
                        value={person.phone}
                        onChange={(e) => onPickupPersonChange(index, 'phone', e.target.value)}
                        placeholder="Phone number"
                        className="border-blue-200"
                        data-testid={`pickup-phone-${index}`}
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-blue-700">Email</Label>
                      <Input
                        type="email"
                        value={person.email || ''}
                        onChange={(e) => onPickupPersonChange(index, 'email', e.target.value)}
                        placeholder="Email address"
                        className="border-blue-200"
                        data-testid={`pickup-email-${index}`}
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-blue-700">Relationship</Label>
                      <Input
                        value={person.relationship || ''}
                        onChange={(e) => onPickupPersonChange(index, 'relationship', e.target.value)}
                        placeholder="e.g. Mother, Father, Aunt"
                        className="border-blue-200"
                        data-testid={`pickup-relationship-${index}`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <Label className="text-sm text-blue-700">Restrictions</Label>
                      <Input
                        value={person.restrictions}
                        onChange={(e) => onPickupPersonChange(index, 'restrictions', e.target.value)}
                        placeholder="Any pickup restrictions"
                        className="border-blue-200"
                        data-testid={`pickup-restrictions-${index}`}
                      />
                    </div>
                    <div className="md:col-span-2 flex items-center gap-3 pt-1">
                      <Switch
                        checked={person.authorizedForRelease || false}
                        onCheckedChange={(checked) => onPickupPersonChange(index, 'authorizedForRelease', checked)}
                        data-testid={`pickup-release-${index}`}
                      />
                      <Label className="text-sm text-blue-700 font-medium cursor-pointer">
                        Authorized for Child Release
                      </Label>
                    </div>
                    {person.authorizedForRelease && (
                      <div className="md:col-span-2">
                        <Label className="text-sm text-blue-700">Address</Label>
                        <Input
                          value={person.address || ''}
                          onChange={(e) => onPickupPersonChange(index, 'address', e.target.value)}
                          placeholder="Street, City, State, ZIP"
                          className="border-blue-200"
                          data-testid={`pickup-address-${index}`}
                        />
                      </div>
                    )}
                  </div>
                  {index > 0 && (
                    <Button
                      type="button"
                      onClick={() => onRemovePickupPerson(index)}
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`remove-pickup-${index}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
            {formData.pickupPersons.length < 2 && (
              <button
                type="button"
                onClick={onAddPickupPerson}
                className="text-sm text-blue-600 hover:text-blue-800 underline cursor-pointer"
                data-testid="add-parent2-btn"
              >
                + Add second parent
              </button>
            )}
          </div>

          {/* ── Guardians Section ── */}
          <div className="flex items-center justify-between mt-6 mb-3">
            <Label className="text-blue-800">Guardian / Pickup</Label>
            <Button
              type="button"
              onClick={onAddAuthorizedPickup}
              variant="outline"
              size="sm"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="add-guardian-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Guardian / Pickup
            </Button>
          </div>
          <div className="space-y-4">
            {formData.pickupPersons.slice(2).length === 0 ? (
              <p className="text-sm text-gray-400 italic">None added</p>
            ) : (
              formData.pickupPersons.slice(2).map((person, i) => {
                const index = i + 2;
                return (
                  <Card key={index} className="border-blue-200">
                    <CardContent className="pt-4">
                      <p className="text-xs font-semibold text-blue-500 uppercase tracking-wide mb-3">
                        Guardian / Pickup
                      </p>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        <div>
                          <Label className="text-sm text-blue-700">Name</Label>
                          <Input
                            value={person.name}
                            onChange={(e) => onPickupPersonChange(index, 'name', e.target.value)}
                            placeholder="Person's name"
                            className="border-blue-200"
                            data-testid={`pickup-name-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-blue-700">Phone</Label>
                          <Input
                            value={person.phone}
                            onChange={(e) => onPickupPersonChange(index, 'phone', e.target.value)}
                            placeholder="Phone number"
                            className="border-blue-200"
                            data-testid={`pickup-phone-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-blue-700">Email</Label>
                          <Input
                            type="email"
                            value={person.email || ''}
                            onChange={(e) => onPickupPersonChange(index, 'email', e.target.value)}
                            placeholder="Email address"
                            className="border-blue-200"
                            data-testid={`pickup-email-${index}`}
                          />
                        </div>
                        <div>
                          <Label className="text-sm text-blue-700">Relationship</Label>
                          <Input
                            value={person.relationship || ''}
                            onChange={(e) => onPickupPersonChange(index, 'relationship', e.target.value)}
                            placeholder="e.g. Aunt, Grandparent"
                            className="border-blue-200"
                            data-testid={`pickup-relationship-${index}`}
                          />
                        </div>
                        <div className="md:col-span-2">
                          <Label className="text-sm text-blue-700">Restrictions</Label>
                          <Input
                            value={person.restrictions}
                            onChange={(e) => onPickupPersonChange(index, 'restrictions', e.target.value)}
                            placeholder="Any pickup restrictions"
                            className="border-blue-200"
                            data-testid={`pickup-restrictions-${index}`}
                          />
                        </div>
                        <div className="md:col-span-2 flex items-center gap-3 pt-1">
                          <Switch
                            checked={person.authorizedForRelease || false}
                            onCheckedChange={(checked) => onPickupPersonChange(index, 'authorizedForRelease', checked)}
                            data-testid={`pickup-release-${index}`}
                          />
                          <Label className="text-sm text-blue-700 font-medium cursor-pointer">
                            Authorized for Child Release
                          </Label>
                        </div>
                        {person.authorizedForRelease && (
                          <div className="md:col-span-2">
                            <Label className="text-sm text-blue-700">Address</Label>
                            <Input
                              value={person.address || ''}
                              onChange={(e) => onPickupPersonChange(index, 'address', e.target.value)}
                              placeholder="Street, City, State, ZIP"
                              className="border-blue-200"
                              data-testid={`pickup-address-${index}`}
                            />
                          </div>
                        )}
                      </div>
                      <Button
                        type="button"
                        onClick={() => onRemovePickupPerson(index)}
                        variant="ghost"
                        size="sm"
                        className="mt-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                        data-testid={`remove-pickup-${index}`}
                      >
                        <Trash2 className="w-4 h-4 mr-1" />
                        Remove
                      </Button>
                    </CardContent>
                  </Card>
                );
              })
            )}
          </div>
        </div>


        {/* Emergency Contacts Section */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <Label className="text-blue-800">Emergency Contacts (Call Only)</Label>
            <Button
              type="button"
              onClick={onAddEmergencyContact}
              variant="outline"
              size="sm"
              className="border-blue-300 text-blue-700 hover:bg-blue-50"
              data-testid="add-emergency-contact-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Contact
            </Button>
          </div>
          <p className="text-xs text-gray-500">People to call in an emergency who may not pick up the child</p>
          <div className="space-y-3">
            {(formData.emergencyContacts || []).map((contact, index) => (
              <Card key={index} className="border-blue-200">
                <CardContent className="pt-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                      <Label className="text-sm text-blue-700">Name</Label>
                      <Input
                        value={contact.name}
                        onChange={(e) => onEmergencyContactChange(index, 'name', e.target.value)}
                        placeholder="Contact name"
                        className="border-blue-200"
                        data-testid={`emergency-name-${index}`}
                      />
                    </div>
                    <div>
                      <Label className="text-sm text-blue-700">Phone</Label>
                      <Input
                        value={contact.phone}
                        onChange={(e) => onEmergencyContactChange(index, 'phone', e.target.value)}
                        placeholder="Phone number"
                        className="border-blue-200"
                        data-testid={`emergency-phone-${index}`}
                      />
                    </div>
                  </div>
                  {formData.emergencyContacts.length > 1 && (
                    <Button
                      type="button"
                      onClick={() => onRemoveEmergencyContact(index)}
                      variant="ghost"
                      size="sm"
                      className="mt-2 text-red-600 hover:text-red-700 hover:bg-red-50"
                      data-testid={`remove-emergency-${index}`}
                    >
                      <Trash2 className="w-4 h-4 mr-1" />
                      Remove
                    </Button>
                  )}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>

        {/* Health Insurance Section */}
        <div className="space-y-3">
          <Label className="text-blue-800">Health Insurance Information</Label>
          <Card className="border-blue-200">
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <Label className="text-sm text-blue-700">Insurance Provider</Label>
                  <Input
                    value={formData.healthInsurance?.provider || ''}
                    onChange={(e) => onHealthInsuranceChange('provider', e.target.value)}
                    placeholder="e.g. Blue Cross Blue Shield"
                    className="border-blue-200"
                    data-testid="insurance-provider"
                  />
                </div>
                <div>
                  <Label className="text-sm text-blue-700">Policy Number</Label>
                  <Input
                    value={formData.healthInsurance?.policyNumber || ''}
                    onChange={(e) => onHealthInsuranceChange('policyNumber', e.target.value)}
                    placeholder="Policy number"
                    className="border-blue-200"
                    data-testid="insurance-policy"
                  />
                </div>
                <div>
                  <Label className="text-sm text-blue-700">Group Number (Optional)</Label>
                  <Input
                    value={formData.healthInsurance?.groupNumber || ''}
                    onChange={(e) => onHealthInsuranceChange('groupNumber', e.target.value)}
                    placeholder="Group number"
                    className="border-blue-200"
                    data-testid="insurance-group"
                  />
                </div>
                <div>
                  <Label className="text-sm text-blue-700">Subscriber Name</Label>
                  <Input
                    value={formData.healthInsurance?.subscriberName || ''}
                    onChange={(e) => onHealthInsuranceChange('subscriberName', e.target.value)}
                    placeholder="Name on policy"
                    className="border-blue-200"
                    data-testid="insurance-subscriber"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Additional Medical Information — allergies, disabilities, emergency info, medications */}
        <div className="space-y-3">
          <Label className="text-blue-800">Additional Medical Information</Label>
          <Card className="border-blue-200">
            <CardContent className="pt-4 space-y-3">
              <div>
                <Label className="text-sm text-blue-700">Allergies</Label>
                <Input
                  value={formData.allergies || ''}
                  onChange={(e) => setFormData({ ...formData, allergies: e.target.value })}
                  placeholder="e.g. Peanuts, penicillin"
                  className="border-blue-200"
                  data-testid="child-allergies-input"
                />
              </div>
              <div>
                <Label className="text-sm text-blue-700">Special Disabilities / Accommodations</Label>
                <Input
                  value={formData.specialDisabilities || ''}
                  onChange={(e) => setFormData({ ...formData, specialDisabilities: e.target.value })}
                  placeholder="Any documented disabilities or accommodations"
                  className="border-blue-200"
                  data-testid="child-special-disabilities-input"
                />
              </div>
              <div>
                <Label className="text-sm text-blue-700">Medical Emergency Information</Label>
                <Input
                  value={formData.medicalEmergencyInfo || ''}
                  onChange={(e) => setFormData({ ...formData, medicalEmergencyInfo: e.target.value })}
                  placeholder="Emergency medical info / instructions"
                  className="border-blue-200"
                  data-testid="child-medical-emergency-info-input"
                />
              </div>
              <div>
                <Label className="text-sm text-blue-700">Medication / Special Conditions</Label>
                <Input
                  value={formData.medicationSpecialConditions || ''}
                  onChange={(e) => setFormData({ ...formData, medicationSpecialConditions: e.target.value })}
                  placeholder="Current medications or special conditions"
                  className="border-blue-200"
                  data-testid="child-medication-special-conditions-input"
                />
              </div>
              <div>
                <Label className="text-sm text-blue-700">Special Needs Notes</Label>
                <Input
                  value={formData.specialNeedsNotes || ''}
                  onChange={(e) => setFormData({ ...formData, specialNeedsNotes: e.target.value })}
                  placeholder="Additional special needs notes"
                  className="border-blue-200"
                  data-testid="child-special-needs-notes-input"
                />
              </div>
              <p className="text-xs text-blue-600 italic">
                Auto-filled from Emergency Contact PDF when uploaded. Manually editable.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Physician / Medical Care Provider — used on excursion reports and emergency dispatch */}
        <div className="space-y-3">
          <Label className="text-blue-800">Physician / Medical Care Provider</Label>
          <Card className="border-blue-200">
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div className="md:col-span-2">
                  <Label className="text-sm text-blue-700">Doctor's Name</Label>
                  <Input
                    value={formData.physician?.name || ''}
                    onChange={(e) => onPhysicianChange('name', e.target.value)}
                    placeholder="e.g. Dr. Jane Smith"
                    className="border-blue-200"
                    data-testid="physician-name"
                  />
                </div>
                <div>
                  <Label className="text-sm text-blue-700">Phone</Label>
                  <Input
                    value={formData.physician?.phone || ''}
                    onChange={(e) => onPhysicianChange('phone', e.target.value)}
                    placeholder="(555) 555-5555"
                    className="border-blue-200"
                    data-testid="physician-phone"
                  />
                </div>
                <div>
                  <Label className="text-sm text-blue-700">Address</Label>
                  <Input
                    value={formData.physician?.address || ''}
                    onChange={(e) => onPhysicianChange('address', e.target.value)}
                    placeholder="Street, City, State ZIP"
                    className="border-blue-200"
                    data-testid="physician-address"
                  />
                </div>
              </div>
              <p className="text-xs text-blue-600 mt-2 italic">
                Auto-filled from Emergency Contact PDF when uploaded. Manually editable.
              </p>
            </CardContent>
          </Card>
        </div>


        <div className="flex gap-3">
          <Button
            type="submit"
            className="flex-1 bg-blue-600 hover:bg-blue-700 text-white rounded-full"
            data-testid="submit-child-btn"
          >
            {editingChild ? 'Update Child' : 'Add Child'}
          </Button>
          <Button
            type="button"
            onClick={() => onOpenChange(false)}
            variant="outline"
            className="flex-1 border-blue-300 text-blue-700 rounded-full"
          >
            Cancel
          </Button>
        </div>
      </form>
    </DialogContent>
  );
};

export default ChildForm;
